import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, IconButton, Select, MenuItem, Avatar, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import {api}from '../services/api';
import Layout from '../layout/layout';
import { useTheme } from "@mui/material/styles";
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';

const WorkflowStep = ({ step, onApproverChange, selectedApprover }) => {
  const [approvers, setApprovers] = useState([]);
    const userId = sessionStorage.getItem('userId');


  useEffect(() => {
    const fetchApprovers = async () => {
      try {
        const response = await api.get(`/Account/manageUsers/${userId}/${step.roleId}`, {
          headers: {
            'X-CORRELATION-ID': 'e291e1c5-1b03-4dd5-a352-e17413f6ff69',
            'accept': 'text/plain'
          }
        });
        setApprovers(response.data);
      } catch (error) {
        console.error('Error fetching approvers:', error);
      }
    };

    fetchApprovers();
  }, [step.roleId, userId]);

  const isApproved = selectedApprover || step.status === 'Approved';
  const statusText = selectedApprover ? "Assigned" : step.status;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, position: 'relative' }}>
      {/* Icon and connecting line */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2 }}>
        <CheckCircleIcon sx={{ color: isApproved ? 'green' : 'orange', zIndex: 1 }} />
        <Box 
          sx={{
            width: '2px',
            height: '100%',
            backgroundColor: 'gray',
            position: 'absolute',
            top: 24, // Adjust to align with the icon
            left: 14, // Adjust to match the icon center
            zIndex: 0
          }}
        />
      </Box>
      
      {/* Step details */}
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {step.role} Approval
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Application: {step.applicationName}
        </Typography>
        <Typography variant="body2">
          Resource: {step.resourceName}
        </Typography>
        <Typography variant="body2">
         Level: {step.stepOrder}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            <Chip 
              label={statusText}
              sx={{
                mr: 1,
                backgroundColor: selectedApprover ? '#2b61aa' : step.status === 'Approved' ? 'green' : 'orange',
                color: 'white'
              }}
            />
          </Typography>
          
          {/* Conditionally render the Avatar only when selectedApprover is present */}
          {selectedApprover && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Avatar 
                alt={step.userName} 
                src={step.userImage} 
                sx={{
                  width: 24, 
                  height: 24, 
                  backgroundColor: '#2b61aa',
                  color: 'white'
                }} 
              />
            </motion.div>
          )}
        </Box>

        {step.status === 'Pending' && (
          <Select
            value={selectedApprover || ""}
            onChange={(event) => onApproverChange(event, step.id)}
            displayEmpty
            fullWidth
            sx={{ mt: 1 }}
          >
            <MenuItem value="" disabled>Select an Approver</MenuItem>
            {approvers.map((approver) => (
              <MenuItem key={approver.userId} value={approver.userId}>{approver.name}</MenuItem>
            ))}
          </Select>
        )}
      </Box>
    </Box>
  );
};


const AssignApproval = () => {
        const theme = useTheme();
  
  const [unAssignedRequests, setUnAssignedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const userId = sessionStorage.getItem('userId');
  const [openDialog, setOpenDialog] = useState(false);
  const [approverDetails, setApproverDetails] = useState([]);
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedApprovers, setSelectedApprovers] = useState({});

const [savingApprovers, setSavingApprovers] = useState(false);


 
    const fetchUnAssignedRequests = async () => {
      try {
        const response = await api.get('/api/Resources/unAssignedRequest', {
          params: { userId: userId },
          headers: { 'X-CORRELATION-ID': 'e291e1c5-1b03-4dd5-a352-e17413f6ff69', 'accept': 'text/plain' }
        });
        setUnAssignedRequests(response.data);
      } catch (error) {
        console.error('Error fetching unAssignedRequests:', error);
      } finally {
        setLoading(false);
      }
    };
 useEffect(() => {
    fetchUnAssignedRequests();
  }, []);

  const fetchApproverDetails = async (resourceReqId) => {
    setLoadingDetails(true);
    try {
      const response = await api.get(`/api/Resources/assignedApprovalConfig/${resourceReqId}`, {
        params: { userId: userId },
        headers: { 'X-CORRELATION-ID': 'e291e1c5-1b03-4dd5-a352-e17413f6ff69', 'accept': 'text/plain' }
      });
      setApproverDetails(response.data);
    } catch (error) {
      console.error('Error fetching approver details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleAssignApproverClick = (resourceId) => {
    setSelectedResourceId(resourceId);
    setOpenDialog(true);
    fetchApproverDetails(resourceId);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setApproverDetails([]);
    setSelectedResourceId(null);
    setSelectedApprovers({});
  };

  const handleApproverChange = (event, stepId) => {
  const selectedUserId = event.target.value;

  setSelectedApprovers((prev) => ({
    ...prev,
    [stepId]: selectedUserId,
  }));
};


 const handleSaveApprover = async () => {
    setSavingApprovers(true); // Start loading

  const userId = Number.parseInt(sessionStorage.getItem('userId'), 10);

  // Construct the payload
  const assignApproversPayload = Object.entries(selectedApprovers).map(([stepId, approverUserId]) => {
    const approverDetail = approverDetails.find(detail => detail.id.toString() === stepId);
    return {
      resourceReqId: selectedResourceId,
      userId: approverUserId, // Use the selected approver's userId
      approvalId: approverDetail.approvalId,
      roleId: approverDetail.roleId,
      stepOrder: approverDetail.stepOrder
    };
  });

  const payload = {
    assignApprovers: assignApproversPayload
  };

  try {
    const response = await api.post(`/api/Resources/AssignApprover`, payload, {
      params: { userId: userId },
      headers: {
        'X-CORRELATION-ID': 'e291e1c5-1b03-4dd5-a352-e17413f6ff69',
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      }
    });

  
        await fetchUnAssignedRequests();

    // Handle success scenario, e.g., show a success message or update UI
  } catch (error) {
    console.error('Error assigning approvers:', error);
    // Handle error scenario, e.g., show an error message
  }  finally {
    setSavingApprovers(false); // Stop loading
    handleCloseDialog();
  }
};


  // Extracted status color mapping to reduce complexity
  const statusColorMap = (status, theme) => ({
    "Approved": { border: theme.palette.success.main, color: theme.palette.success.main, bg: theme.palette.success.light },
    "Pending": { border: theme.palette.warning.main, color: theme.palette.warning.main, bg: theme.palette.warning.light },
    "Scheduled": { border: theme.palette.success.main, color: theme.palette.success.main, bg: theme.palette.success.light },
    "Hold": { border: theme.palette.warning.main, color: theme.palette.warning.main, bg: theme.palette.warning.light },
    "Reject": { border: theme.palette.error.main, color: theme.palette.error.main, bg: theme.palette.error.light },
  });

  const getStatusStyles = (status, theme) => {
    const colors = statusColorMap(status, theme);
    return colors[status] || { border: 'transparent', color: 'transparent', bg: 'transparent' };
  };

  const columns = [
    { field: 'applicationName', headerName: 'Application Name', flex: 1 },
    { field: 'resourceName', headerName: 'Resource Name', flex: 1 },
    { field: 'approverRoles', headerName: 'Approver Roles', flex: 1 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      sortable: false, 
      filterable: false,

      disableColumnMenu: true,
      renderCell: (params) => {
        const styles = getStatusStyles(params.value, theme);
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '25px',
              textAlign: "center",
              borderRadius: '5px',
              borderWidth: '1px',
              borderStyle: 'solid',
              mt: '13px',
              borderColor: styles.border,
              color: styles.color,
              bgcolor: styles.bg,
            }}
          >
            <Typography sx={{ px: 1, borderRadius: 1 }}>
              {params.value}
            </Typography>
          </Box>
        );
      }
    },    
    { field: 'currentVerifier', headerName: 'Current Verifier', flex: 1 },
    { field: 'requester', headerName: 'Requester', flex: 1 },
    {
      field: 'assignApprover',
      headerName: 'Assign Approver',
      flex: 1,
      renderCell: (params) => (
        <Button variant="contained" onClick={() => handleAssignApproverClick(params.row.resource_Id)}>Assign Approver</Button>
      )
    }
  ];

  return (
    <Layout pageType="sideBar">
      <Box sx={{ flexGrow: 1, padding: 2 }}>
        <Typography variant="h5" gutterBottom>
          Delegate Approver
        </Typography>
        {loading ? (
          <Typography><CircularProgress /></Typography>
        ) : (
          <DataGrid
            rows={unAssignedRequests}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            disableSelectionOnClick
            hideFooterSelectedRowCount 
            getRowId={(row) => row.resource_Id}
            sx={{
              '& .MuiDataGrid-root': {
                bgcolor: '#ffffff',
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: '#e0e0e0',
              },
              '& .MuiDataGrid-cell': {
                borderColor: '#e0e0e0',
              },
            }}
          />
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontSize: '18px', fontWeight: 'Bold'}}>
            Approver Configuration
            <IconButton aria-label="close" onClick={handleCloseDialog} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent>
            {loadingDetails && <CircularProgress />}
            
            {!loadingDetails && approverDetails.length > 0 && (
              approverDetails.map((step) => (
                <WorkflowStep
                  key={step.id}
                  step={step}
                  approvers={approverDetails}
                  onApproverChange={handleApproverChange}
                  selectedApprover={selectedApprovers[step.id] || ""}
                />
              ))
            )}

            {!loadingDetails && approverDetails.length === 0 && (
              <Typography>No approver details available.</Typography>
            )}
          </DialogContent>

          <DialogActions>
            {savingApprovers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Button onClick={handleCloseDialog} color="secondary" variant="cancelContained">
                  Cancel
                </Button>
                <Button onClick={handleSaveApprover} variant="greenContained" color="primary" disabled={!Object.values(selectedApprovers).some(Boolean)}>
                  Assign Approver
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

WorkflowStep.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    role: PropTypes.string,
    applicationName: PropTypes.string,
    resourceName: PropTypes.string,
    stepOrder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    userName: PropTypes.string,
    userImage: PropTypes.string,
  }).isRequired,
  onApproverChange: PropTypes.func.isRequired,
  selectedApprover: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AssignApproval;
